// ═══════════════════════════════════════════════════════════════
// ClearView Philanthropy CRM — Contact Report Section Catalog
// Path: src/constants/contactReportSections.js
//
// Single source of truth for the donor contact report form structure:
//   - MEETING_TYPES: dropdown options for the required "meeting_type" field
//   - OPTIONAL_SECTIONS: collapsible sections shown beneath the 6 required fields
//   - PINNABLE_KEYS: which keys can be pinned to members.pinned_intelligence
//
// Adding a new optional section in v1.x:
//   1. Add an entry to OPTIONAL_SECTIONS
//   2. If pinnable, also list its pinKey in PINNABLE_KEYS
//   3. The form, the print view, and the sidebar Donor Notes panel
//      will pick it up automatically
//
// Blueprint: src/docs/CRM_Donor_Contact_Reports_Blueprint_v1_0.md
// ═══════════════════════════════════════════════════════════════

// ── Meeting type dropdown (required field) ─────────────────────
export const MEETING_TYPES = [
  { value: "discovery",    label: "Discovery" },
  { value: "cultivation",  label: "Cultivation" },
  { value: "solicitation", label: "Solicitation" },
  { value: "stewardship",  label: "Stewardship" },
];

// ── Optional intelligence sections (collapsible) ───────────────
// Each section is rendered as a collapsible panel in the form.
// `pinnable: true` means the section header gets a "📌 Pin to donor record" checkbox.
// `split: true` means the section has two text fields (used by Messaging only in v1).
export const OPTIONAL_SECTIONS = [
  {
    key: "family",
    label: "Family",
    placeholder: "Spouse, children, parents, key relationships, milestones",
    pinnable: true,
    pinKey: "family",
  },
  {
    key: "occupation",
    label: "Occupation",
    placeholder: "Role, employer, transitions, retirement plans, business interests",
    pinnable: true,
    pinKey: "occupation",
  },
  {
    key: "recreation",
    label: "Recreation & Interests",
    placeholder: "Hobbies, sports, travel, the things that light them up",
    pinnable: true,
    pinKey: "recreation",
  },
  {
    key: "messaging",
    label: "Messaging Emphasis & Constraints",
    split: true,
    fields: [
      {
        key: "emphasize",
        label: "Emphasize",
        placeholder: "Themes, programs, language that resonates",
        pinnable: true,
        pinKey: "messaging_emphasize",
      },
      {
        key: "avoid",
        label: "Avoid",
        placeholder: "Topics, language, or stories to steer clear of",
        pinnable: true,
        pinKey: "messaging_avoid",
      },
    ],
  },
  {
    key: "wealth_signals",
    label: "Wealth Signals",
    placeholder: "Liquidity events, business sales, real estate, inheritance signals",
    pinnable: false,
  },
  {
    key: "philanthropic",
    label: "Philanthropic Interests",
    placeholder: "Other organizations supported, causes mentioned, family foundation activity",
    pinnable: false,
  },
  {
    key: "network",
    label: "Network & Introductions",
    placeholder: "People they mentioned, intros they offered, board service",
    pinnable: false,
  },
  {
    key: "capacity_update",
    label: "Capacity Update",
    placeholder: "Updated capacity rating and rationale (narrative only in v1)",
    pinnable: false,
  },
  {
    key: "strategic_note",
    label: "Strategic Note",
    placeholder: "The single most important insight from this visit",
    pinnable: false,
  },
];

// ── All pin keys (for sidebar Donor Notes panel iteration) ────
// The order here determines the display order in the sidebar.
export const PINNABLE_KEYS = [
  { key: "family",              label: "Family" },
  { key: "occupation",          label: "Occupation" },
  { key: "recreation",          label: "Recreation & Interests" },
  { key: "messaging_emphasize", label: "Messaging — Emphasize" },
  { key: "messaging_avoid",     label: "Messaging — Avoid" },
];

// ── Required field metadata (for form validation) ─────────────
export const REQUIRED_FIELDS = [
  "visitDate",
  "meetingType",
  "attendees",
  "summary",
  "nextStep",
  "nextStepDate",
];
